import { useEffect, useState } from "react";
import { EmailOutbox } from "@/components/settings/EmailOutbox";
import { TestEmailNotice } from "@/components/settings/TestEmailNotice";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  useHrNotificationRecipients,
  useSetHrNotificationRecipients,
} from "@/hooks/use-hr-notification";
import type { HrRecipientPurpose } from "@/schemas/hr-notification";

// Split a comma/newline/semicolon list into trimmed, de-duped addresses.
function parseEmails(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[,\n;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
}

// Loose client-side email check — the server validates strictly.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Settings → Email Outbox: the recipient fields at the top — one list per
 * notification purpose, each independent — then the sent-email outbox below on
 * the same page. An empty list simply means nobody is notified for that event;
 * nothing else changes.
 */
export function SettingsEmailOutboxPage() {
  return (
    <div className="space-y-8">
      <TestEmailNotice />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <RecipientListEditor
          purpose="change_notice"
          title="Profile edits"
          description="Emailed when someone updates their own contact info (personal email, phone, location, working hours, or photo). Separate multiple addresses with commas."
        />
        <RecipientListEditor
          purpose="update_request"
          title="Update requests"
          description="Receives a person's “Request update” message from their profile (e.g. asking to change their manager). Can be a different team than the one above."
        />
        <RecipientListEditor
          purpose="error_logged"
          title="Errors captured"
          description="Emailed the first time a new error is captured. Repeats of the same error don't send again — the running count lives on the Error log tab."
        />
      </div>

      <section className="space-y-3 border-t pt-6">
        <div>
          <h2 className="font-semibold text-foreground">Sent email</h2>
          <p className="text-sm text-muted-foreground">
            The most recent transactional emails sent, newest first — every notification above, plus
            anything else the app sends. Click a row to see the message that was sent. Read-only;
            delivery status is updated by the background workers.
          </p>
        </div>
        <EmailOutbox />
      </section>
    </div>
  );
}

function RecipientListEditor({
  purpose,
  title,
  description,
}: {
  purpose: HrRecipientPurpose;
  title: string;
  description: string;
}) {
  const {
    data: recipients = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useHrNotificationRecipients(purpose);
  const save = useSetHrNotificationRecipients(purpose);

  const [text, setText] = useState("");
  const saved = recipients.map((r) => r.email).join(", ");
  useEffect(() => {
    setText(saved);
  }, [saved]);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load recipients"}
        onRetry={() => refetch()}
      />
    );
  }

  const emails = parseEmails(text);
  const invalid = emails.filter((e) => !EMAIL_RE.test(e));
  const dirty = emails.join(",") !== parseEmails(saved).join(",");

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="hr@example.com, people-ops@example.com"
        className="min-h-[72px]"
        disabled={isLoading}
        aria-invalid={invalid.length > 0}
      />

      {invalid.length > 0 && (
        <p className="text-sm text-danger-foreground">
          Not valid email{invalid.length > 1 ? "s" : ""}: {invalid.join(", ")}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button
          onClick={() => save.mutate(emails)}
          disabled={isLoading || save.isPending || invalid.length > 0 || !dirty}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        {dirty && invalid.length === 0 && (
          <span className="text-sm text-muted-foreground">
            {emails.length} recipient{emails.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </section>
  );
}
